import React from 'react'
import { Box, Chip, LinearProgress, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { AsnReceptionLpnInfoDto } from '../types'

interface ViewDetailStepProps {
  info: AsnReceptionLpnInfoDto
  scannedLpns: string[]
  onClose: () => void
}

const isLong = (val: string) => val.length > 15

const fabStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  border: '1px solid #E0E0E0',
  borderRadius: '12px',
  height: '40px',
  padding: '0 10px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#666'
}

export const ViewDetailStep: React.FC<ViewDetailStepProps> = ({
  info,
  scannedLpns,
  onClose
}) => {
  const totalLpns = info.lpns.length
  const controlled = scannedLpns.length
  const pct = totalLpns > 0 ? Math.round((controlled / totalLpns) * 100) : 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '12px 16px 4px 16px', flexShrink: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 500, color: '#333' }}>
          Listado de LPN
        </Typography>
      </div>
      {/* Barra superior */}
      <Box
        sx={{
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: '6px',
          minHeight: '48px'
        }}
      >
        <Box onClick={onClose} sx={fabStyle}>
          <ArrowBackIcon style={{ fontSize: 18, color: '#1976d2' }} />
          Volver
        </Box>
      </Box>

      {/* Chips proveedor/origen */}
      <Box
        sx={{
          px: 1.5,
          py: '10px',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '6px'
        }}
      >
        {[
          { label: 'Proveedor', value: info.supplierName },
          { label: 'Origen', value: info.supplierOriginName }
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{ gridColumn: isLong(value) ? 'span 2' : 'span 1' }}
          >
            <Chip
              label={`${label}: ${value}`}
              color='secondary'
              variant='outlined'
              style={{ fontSize: '0.8125rem', width: '100%' }}
            />
          </Box>
        ))}
      </Box>

      {/* Barra de progreso */}
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5
          }}
        >
          <Typography variant='body2' sx={{ fontWeight: 600, color: '#555' }}>
            LPNs controladas
          </Typography>
          <Typography
            variant='body2'
            sx={{ fontWeight: 700, color: '#185FA5' }}
          >
            {controlled} / {totalLpns} · {pct}%
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={pct}
          sx={{
            borderRadius: '4px',
            height: '8px',
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': { backgroundColor: '#185FA5' }
          }}
        />
      </Box>

      {/* Lista de LPNs */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5 }}>
        <Typography
          variant='overline'
          sx={{ fontWeight: 700, color: '#555', letterSpacing: 1.5 }}
        >
          Detalle por LPN
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
          {info.lpns.map((lpn) => {
            const received = scannedLpns.includes(lpn)
            return (
              <Box
                key={lpn}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.2,
                  px: 1,
                  borderRadius: '6px',
                  mb: 0.5,
                  backgroundColor: received ? '#f1f8f1' : 'transparent',
                  border: received
                    ? '1px solid #c8e6c9'
                    : '1px solid transparent'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {received ? (
                    <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  ) : (
                    <FiberManualRecordIcon
                      sx={{ color: '#bbb', fontSize: 10 }}
                    />
                  )}
                  <Typography
                    variant='body2'
                    fontWeight={received ? 'bold' : 'normal'}
                    sx={{ color: received ? '#222' : '#888' }}
                  >
                    {lpn}
                  </Typography>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ color: received ? '#3B6D11' : '#888', fontWeight: 500 }}
                >
                  {received ? 'Recibida' : 'Pendiente'}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
